import { useMemo, useRef, useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Link, useParams } from 'react-router'
import {
  ArrowLeft,
  CalendarClock,
  CheckCircle2,
  Circle,
  FileDown,
  FileUp,
  Plus,
  ScrollText,
  X,
} from 'lucide-react'
import {
  COMPETITION_PHASE_LABELS,
  COMPETITION_PUBLIC_STATES,
  PAYMENT_TIMING_LABELS,
  type LifecycleAction,
} from '@concours/shared'
import { ApiRequestError } from '@concours/api-client'
import {
  Alert,
  Badge,
  Button,
  Can,
  Card,
  CardBody,
  CardHeader,
  ConfirmModal,
  Empty,
  InfoGrid,
  InfoItem,
  Input,
  Modal,
  PageHeader,
  StatusBadge,
  STATUS_LABELS,
  Table,
  Tabs,
  Textarea,
  useAuth,
  useToast,
  type Column,
} from '@concours/ui'
import { errorMessage, fieldErrors } from '../../lib/errors'
import {
  formFrom,
  fromLocalInput,
  money,
  payloadFrom,
  toLocalInput,
  when,
  type Category,
  type CompetitionDetail,
  type CompetitionForm,
  type Derogation,
  type HistoryEntry,
  type Milestone,
  type Notice,
} from '../../lib/competition-types'
import { CompetitionFields, type FieldSection } from './CompetitionFields'
import { FormTab } from './FormTab'
import { PaymentTab } from './PaymentTab'
import { COMPETITIONS_KEY } from './CompetitionsPage'

const PHASE_VARIANT = {
  unavailable: 'neutral',
  upcoming: 'info',
  open: 'success',
  suspended: 'warning',
  closed: 'outline',
  results: 'accent',
} as const

/** Fiche d'un concours (TODO phase 6) : cycle de vie, modification, calendrier, avis, historique. */
export function CompetitionPage() {
  const { t } = useTranslation()
  const { id = '' } = useParams()
  const { session } = useAuth()
  const [tab, setTab] = useState('overview')
  const key = [...COMPETITIONS_KEY, id]
  const detail = useQuery({
    queryKey: key,
    queryFn: () => session.api.get<CompetitionDetail>(`/admin/competitions/${id}`),
  })

  if (detail.isError)
    return (
      <>
        <Back />
        <Alert tone="danger">{errorMessage(detail.error)}</Alert>
      </>
    )
  const c = detail.data
  if (!c) return <Back />

  return (
    <>
      <Back />
      <PageHeader
        eyebrow={<span className="mono">{c.reference}</span>}
        title={c.title}
        description={`${c.typeLabel} · ${c.session} · ${c.corps}`}
        actions={
          <span className="row" style={{ gap: 'var(--space-2)', flexWrap: 'wrap' }}>
            <StatusBadge domain="competition" status={c.status} />
            <Badge variant={PHASE_VARIANT[c.window.phase]} dot>
              {COMPETITION_PHASE_LABELS[c.window.phase]}
            </Badge>
          </span>
        }
      />
      {(COMPETITION_PUBLIC_STATES as readonly string[]).includes(c.status) &&
        c.publicationAt &&
        new Date(c.publicationAt) > new Date() && (
          <Alert tone="warning" title="Pas encore visible sur le portail candidat">
            Ce concours est publié, mais il n’apparaîtra aux candidats qu’à partir du{' '}
            {when(c.publicationAt)} (date de publication de l’avis). Pour l’afficher dès maintenant,
            videz ou avancez cette date dans « Modifier ».
          </Alert>
        )}
      <Lifecycle competition={c} />
      <Tabs
        label={t('competitions.title')}
        value={tab}
        onChange={setTab}
        items={[
          { value: 'overview', label: t('competitions.tabs.overview') },
          { value: 'edit', label: t('competitions.tabs.edit') },
          { value: 'calendar', label: t('competitions.tabs.calendar') },
          { value: 'form', label: t('competitions.tabs.form') },
          { value: 'payment', label: 'Paiement' },
          { value: 'notice', label: t('competitions.tabs.notice') },
          { value: 'history', label: t('competitions.tabs.history') },
          { value: 'derogations', label: t('competitions.tabs.derogations') },
        ]}
      />
      <div style={{ marginBlockStart: 'var(--space-5)' }}>
        {tab === 'overview' && <Overview c={c} onGo={setTab} />}
        {tab === 'edit' && <EditTab key={c.updatedAt ?? ''} c={c} />}
        {tab === 'calendar' && <CalendarTab key={c.updatedAt ?? ''} c={c} />}
        {tab === 'notice' && <NoticeTab c={c} />}
        {tab === 'form' && <FormTab key={c.form?.versionId ?? 'none'} c={c} />}
        {tab === 'payment' && <PaymentTab c={c} />}
        {tab === 'history' && <HistoryTab id={c.id} />}
        {tab === 'derogations' && <DerogationsTab c={c} />}
      </div>
    </>
  )
}

function Back() {
  const { t } = useTranslation()
  return (
    <Link
      to="/concours"
      className="btn btn-ghost btn-sm"
      style={{ marginBlockEnd: 'var(--space-3)' }}
    >
      <ArrowLeft aria-hidden="true" /> {t('competitions.back')}
    </Link>
  )
}

/** Met à jour le cache après une action (la réponse est la fiche à jour). */
function useApply(id: string) {
  const queryClient = useQueryClient()
  return async (updated?: CompetitionDetail) => {
    if (updated) queryClient.setQueryData([...COMPETITIONS_KEY, id], updated)
    await queryClient.invalidateQueries({ queryKey: COMPETITIONS_KEY })
  }
}

/* ------------------------------------------------------------------ */
/* Cycle de vie                                                        */
/* ------------------------------------------------------------------ */

function Lifecycle({ competition: c }: { competition: CompetitionDetail }) {
  const { t } = useTranslation()
  const { session } = useAuth()
  const toast = useToast()
  const apply = useApply(c.id)
  const [pending, setPending] = useState<LifecycleAction | null>(null)
  const [extending, setExtending] = useState(false)
  const [problems, setProblems] = useState<string[]>([])

  const transition = useMutation({
    mutationFn: ({ to, reason }: { to: string; reason?: string }) =>
      session.api.post<CompetitionDetail>(`/admin/competitions/${c.id}/transitions`, {
        to,
        reason,
      }),
    onSuccess: async (updated) => {
      setPending(null)
      setProblems([])
      toast.success(
        t('competitions.lifecycle.done', {
          status: STATUS_LABELS.competition[updated.status].label,
        })
      )
      await apply(updated)
    },
    onError: (error) => {
      setPending(null)
      if (error instanceof ApiRequestError && error.code === 'E_NOT_READY')
        setProblems((error.details as { problems: string[] }).problems)
      else toast.error(errorMessage(error))
    },
  })

  if (!c.actions.length && !c.can.extend && !problems.length) return null

  return (
    <div className="stack" style={{ marginBlockEnd: 'var(--space-5)' }}>
      {problems.length > 0 && (
        <Alert tone="warning" title={t('competitions.lifecycle.notReady')}>
          <ul style={{ margin: 0, paddingInlineStart: 'var(--space-5)' }}>
            {problems.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        </Alert>
      )}
      <Card>
        <CardBody>
          <div
            className="row"
            style={{ gap: 'var(--space-2)', flexWrap: 'wrap', alignItems: 'center' }}
          >
            <span className="text-muted" style={{ marginInlineEnd: 'auto' }}>
              {t('competitions.lifecycle.title')}
            </span>
            {c.can.extend && (
              <Button
                variant="secondary"
                icon={<CalendarClock />}
                onClick={() => setExtending(true)}
              >
                {c.status === 'closed'
                  ? t('competitions.lifecycle.reopen')
                  : t('competitions.lifecycle.extend')}
              </Button>
            )}
            {c.actions.map((action) => (
              <Button
                key={action.to}
                variant={action.tone === 'danger' ? 'danger' : 'primary'}
                loading={transition.isPending && transition.variables?.to === action.to}
                onClick={() => setPending(action)}
              >
                {action.label}
              </Button>
            ))}
          </div>
        </CardBody>
      </Card>
      {pending && (
        <ConfirmModal
          open
          onClose={() => setPending(null)}
          title={t('competitions.lifecycle.confirm', { action: pending.label.toLowerCase() })}
          message={t(`competitions.lifecycle.explain.${pending.to}`, {
            defaultValue: t('competitions.lifecycle.explainDefault'),
          })}
          tone={pending.tone}
          confirmLabel={pending.label}
          reasonRequired={pending.reasonRequired}
          onConfirm={async (reason) => {
            await transition.mutateAsync({ to: pending.to, reason }).catch(() => undefined)
          }}
        />
      )}
      <ExtendModal open={extending} onClose={() => setExtending(false)} c={c} />
    </div>
  )
}

function ExtendModal({
  open,
  onClose,
  c,
}: {
  open: boolean
  onClose: () => void
  c: CompetitionDetail
}) {
  const { t } = useTranslation()
  const { session } = useAuth()
  const toast = useToast()
  const apply = useApply(c.id)
  const [closesAt, setClosesAt] = useState(toLocalInput(c.closesAt))
  const [deadline, setDeadline] = useState(toLocalInput(c.paymentDeadlineAt))
  const [reason, setReason] = useState('')
  const extend = useMutation({
    mutationFn: () =>
      session.api.post<CompetitionDetail>(`/admin/competitions/${c.id}/extend`, {
        closesAt: fromLocalInput(closesAt),
        ...(c.paymentTiming !== 'not_required'
          ? { paymentDeadlineAt: fromLocalInput(deadline) }
          : {}),
        reason,
      }),
    onSuccess: async (updated) => {
      toast.success(t('competitions.lifecycle.extended'))
      onClose()
      await apply(updated)
    },
    onError: (error) => {
      if (!Object.keys(fieldErrors(error)).length) toast.error(errorMessage(error))
    },
  })
  const errors = fieldErrors(extend.error)
  const submit = (e: FormEvent) => {
    e.preventDefault()
    extend.mutate()
  }
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={
        c.status === 'closed'
          ? t('competitions.lifecycle.reopen')
          : t('competitions.lifecycle.extend')
      }
      description={t('competitions.lifecycle.extendText', { date: when(c.closesAt) })}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('rbac.create.cancel')}
          </Button>
          <Button
            variant="primary"
            type="submit"
            form="extend-form"
            loading={extend.isPending}
            disabled={reason.trim().length < 10}
          >
            {t('competitions.lifecycle.extendSubmit')}
          </Button>
        </>
      }
    >
      <form id="extend-form" className="stack" onSubmit={submit}>
        <Input
          label={t('competitions.fields.closesAt')}
          type="datetime-local"
          required
          value={closesAt}
          error={errors.closesAt}
          onChange={(e) => setClosesAt(e.target.value)}
        />
        {c.paymentTiming !== 'not_required' && (
          <Input
            label={t('competitions.fields.paymentDeadlineAt')}
            type="datetime-local"
            value={deadline}
            error={errors.paymentDeadlineAt}
            onChange={(e) => setDeadline(e.target.value)}
          />
        )}
        <Textarea
          label={t('rbac.user.reason')}
          required
          rows={3}
          hint={t('competitions.reasonHint')}
          value={reason}
          error={errors.reason}
          onChange={(e) => setReason(e.target.value)}
        />
      </form>
    </Modal>
  )
}

/* ------------------------------------------------------------------ */
/* Aperçu                                                              */
/* ------------------------------------------------------------------ */

function Overview({ c, onGo }: { c: CompetitionDetail; onGo: (tab: string) => void }) {
  const { t } = useTranslation()
  // Indicatif : la publication est revérifiée par l'API (E_NOT_READY).
  const checklist = [
    { ok: Boolean(c.opensAt && c.closesAt), label: t('competitions.check.period'), tab: 'edit' },
    {
      ok: Boolean(c.closesAt && new Date(c.closesAt) > new Date()),
      label: t('competitions.check.future'),
      tab: 'edit',
    },
    {
      ok: c.paymentTiming === 'not_required' || Boolean(c.paymentDeadlineAt),
      label: t('competitions.check.deadline'),
      tab: 'edit',
    },
    { ok: Boolean(c.conditions?.trim()), label: t('competitions.check.conditions'), tab: 'edit' },
    { ok: Boolean(c.notice), label: t('competitions.check.notice'), tab: 'notice' },
    { ok: Boolean(c.form), label: t('competitions.check.form'), tab: 'form' },
  ]
  return (
    <div className="stack" style={{ gap: 'var(--space-5)' }}>
      {c.status === 'draft' && (
        <Card>
          <CardHeader title={t('competitions.check.title')} />
          <CardBody>
            <ul className="checklist">
              {checklist.map((item) => (
                <li key={item.label} data-ok={item.ok}>
                  {item.ok ? (
                    <CheckCircle2 aria-hidden="true" size={18} />
                  ) : (
                    <Circle aria-hidden="true" size={18} />
                  )}
                  <span>
                    <span className="sr-only">
                      {item.ok ? t('competitions.check.ok') : t('competitions.check.todo')} :{' '}
                    </span>
                    {item.label}{' '}
                    {!item.ok && (
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        onClick={() => onGo(item.tab)}
                      >
                        {t('competitions.check.fix')}
                      </button>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          </CardBody>
        </Card>
      )}
      <Card>
        <CardHeader title={t('competitions.tabs.overview')} />
        <CardBody>
          <InfoGrid>
            <InfoItem label={t('competitions.fields.seats')}>{c.seats}</InfoItem>
            <InfoItem label={t('competitions.fields.grade')}>{c.grade ?? '—'}</InfoItem>
            <InfoItem label={t('competitions.fields.prefix')}>
              <span className="mono">{`${c.applicationPrefix}-${c.year}-XXXXXX`}</span>
            </InfoItem>
            <InfoItem label={t('competitions.fields.publicationAt')}>
              {when(c.publicationAt)}
            </InfoItem>
            <InfoItem label={t('competitions.fields.opensAt')}>{when(c.opensAt)}</InfoItem>
            <InfoItem label={t('competitions.fields.closesAt')}>{when(c.closesAt)}</InfoItem>
            <InfoItem label={t('competitions.fields.paymentTiming')}>
              {PAYMENT_TIMING_LABELS[c.paymentTiming]}
            </InfoItem>
            <InfoItem label={t('competitions.fields.feeAmount')}>
              {c.feeAmount ? money(c.feeAmount, c.currency) : '—'}
            </InfoItem>
            <InfoItem label={t('competitions.fields.paymentDeadlineAt')}>
              {when(c.paymentDeadlineAt)}
            </InfoItem>
            <InfoItem label={t('competitions.tabs.derogations')}>{c.activeDerogations}</InfoItem>
          </InfoGrid>
          {c.conditions && (
            <section style={{ marginBlockStart: 'var(--space-5)' }}>
              <h3 className="audit-h">{t('competitions.fields.conditions')}</h3>
              <p style={{ whiteSpace: 'pre-line', margin: 0 }}>{c.conditions}</p>
            </section>
          )}
          {c.requiredDocuments.length > 0 && (
            <section style={{ marginBlockStart: 'var(--space-5)' }}>
              <h3 className="audit-h">{t('competitions.fields.requiredDocuments')}</h3>
              <ul style={{ margin: 0 }}>
                {c.requiredDocuments.map((d) => (
                  <li key={d}>{d}</li>
                ))}
              </ul>
            </section>
          )}
        </CardBody>
      </Card>
      <Can perm="audit.view">
        <div>
          <Link
            className="btn btn-secondary"
            to={`/audit?ressource=competition&id=${encodeURIComponent(c.id)}`}
          >
            <ScrollText aria-hidden="true" /> {t('audit.journal')}
          </Link>
        </div>
      </Can>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* Modifier                                                            */
/* ------------------------------------------------------------------ */

const EDIT_SECTIONS: FieldSection[] = ['info', 'conditions', 'calendar', 'fees']

function EditTab({ c }: { c: CompetitionDetail }) {
  const { t } = useTranslation()
  const { session } = useAuth()
  const toast = useToast()
  const apply = useApply(c.id)
  const initial = useMemo(() => formFrom(c), [c])
  const [form, setForm] = useState<CompetitionForm>(initial)
  const [reason, setReason] = useState('')

  const changed = (Object.keys(form) as (keyof CompetitionForm)[]).filter(
    (k) => JSON.stringify(form[k]) !== JSON.stringify(initial[k])
  )
  const save = useMutation({
    mutationFn: () =>
      session.api.patch<CompetitionDetail>(`/admin/competitions/${c.id}`, {
        ...payloadFrom(form, changed),
        ...(c.status !== 'draft' ? { reason } : {}),
      }),
    onSuccess: async (updated) => {
      toast.success(t('rbac.user.done'))
      setReason('')
      await apply(updated)
    },
    onError: (error) => {
      if (!Object.keys(fieldErrors(error)).length) toast.error(errorMessage(error))
    },
  })
  const errors = fieldErrors(save.error)
  const readOnly = !c.can.update
  const locked = readOnly ? ['*'] : c.lockedFields

  return (
    <form
      className="stack"
      style={{ gap: 'var(--space-5)' }}
      onSubmit={(e) => {
        e.preventDefault()
        save.mutate()
      }}
    >
      {c.status !== 'draft' && !readOnly && (
        <Alert tone="info">{t('competitions.edit.lockedInfo')}</Alert>
      )}
      {readOnly && <Alert tone="info">{t('competitions.edit.readOnly')}</Alert>}
      {EDIT_SECTIONS.map((section) => (
        <Card key={section}>
          <CardHeader title={t(`competitions.wizard.step.${section}`)} />
          <CardBody>
            <CompetitionFields
              section={section}
              form={form}
              onChange={(p) => setForm((f) => ({ ...f, ...p }))}
              errors={errors}
              locked={locked}
            />
          </CardBody>
        </Card>
      ))}
      {!readOnly && (
        <Card className="perm-savebar">
          <CardBody>
            <div className="stack" style={{ gap: 'var(--space-3)' }}>
              {c.status !== 'draft' && (
                <Textarea
                  label={t('rbac.user.reason')}
                  required
                  rows={2}
                  hint={t('competitions.reasonHint')}
                  value={reason}
                  error={errors.reason}
                  onChange={(e) => setReason(e.target.value)}
                />
              )}
              <div className="row" style={{ justifyContent: 'space-between' }}>
                <span aria-live="polite" className="text-muted">
                  {changed.length
                    ? t('competitions.edit.changes', { count: changed.length })
                    : t('rbac.editor.noChange')}
                </span>
                <span className="row" style={{ gap: 'var(--space-2)' }}>
                  <Button
                    variant="secondary"
                    disabled={!changed.length}
                    onClick={() => setForm(initial)}
                  >
                    {t('rbac.editor.reset')}
                  </Button>
                  <Button
                    variant="primary"
                    type="submit"
                    loading={save.isPending}
                    disabled={
                      !changed.length || (c.status !== 'draft' && reason.trim().length < 10)
                    }
                  >
                    {t('competitions.edit.save')}
                  </Button>
                </span>
              </div>
            </div>
          </CardBody>
        </Card>
      )}
    </form>
  )
}

/* ------------------------------------------------------------------ */
/* Calendrier et catégories                                            */
/* ------------------------------------------------------------------ */

function CalendarTab({ c }: { c: CompetitionDetail }) {
  const { t } = useTranslation()
  const { session } = useAuth()
  const toast = useToast()
  const apply = useApply(c.id)
  const [milestones, setMilestones] = useState<Milestone[]>(c.milestones)
  const [categories, setCategories] = useState<Category[]>(c.categories)
  const readOnly = !c.can.update

  const saveMilestones = useMutation({
    mutationFn: () =>
      session.api.put<CompetitionDetail>(`/admin/competitions/${c.id}/milestones`, {
        milestones: milestones.map((m) => ({ ...m, endsAt: m.endsAt || null })),
      }),
    onSuccess: async (updated) => {
      toast.success(t('rbac.user.done'))
      await apply(updated)
    },
    onError: (error) => toast.error(errorMessage(error), JSON.stringify(fieldErrors(error))),
  })
  const saveCategories = useMutation({
    mutationFn: () =>
      session.api.put<CompetitionDetail>(`/admin/competitions/${c.id}/categories`, {
        categories: categories.map((cat) => ({ ...cat, seats: cat.seats || null })),
      }),
    onSuccess: async (updated) => {
      toast.success(t('rbac.user.done'))
      await apply(updated)
    },
    onError: (error) =>
      toast.error(errorMessage(error), Object.values(fieldErrors(error)).join(' ')),
  })

  return (
    <div className="stack" style={{ gap: 'var(--space-5)' }}>
      <Card>
        <CardHeader title={t('competitions.calendar.title')} />
        <CardBody>
          <p className="text-muted" style={{ marginBlockStart: 0 }}>
            {t('competitions.calendar.help')}
          </p>
          <fieldset
            disabled={readOnly}
            className="stack"
            style={{ border: 0, padding: 0, gap: 'var(--space-3)' }}
          >
            {milestones.map((m, i) => (
              <div
                key={i}
                className="row"
                style={{ gap: 'var(--space-2)', flexWrap: 'wrap', alignItems: 'flex-end' }}
              >
                <Input
                  label={t('competitions.calendar.label')}
                  value={m.label}
                  fieldClassName="grow"
                  onChange={(e) =>
                    setMilestones(
                      milestones.map((x, j) => (j === i ? { ...x, label: e.target.value } : x))
                    )
                  }
                />
                <Input
                  label={t('competitions.calendar.start')}
                  type="datetime-local"
                  value={toLocalInput(m.startsAt)}
                  onChange={(e) =>
                    setMilestones(
                      milestones.map((x, j) =>
                        j === i ? { ...x, startsAt: fromLocalInput(e.target.value) ?? '' } : x
                      )
                    )
                  }
                />
                <Input
                  label={t('competitions.calendar.end')}
                  type="datetime-local"
                  value={toLocalInput(m.endsAt)}
                  onChange={(e) =>
                    setMilestones(
                      milestones.map((x, j) =>
                        j === i ? { ...x, endsAt: fromLocalInput(e.target.value) } : x
                      )
                    )
                  }
                />
                <label className="check-row" style={{ paddingBlockEnd: 'var(--space-2)' }}>
                  <input
                    type="checkbox"
                    checked={m.isPublic}
                    onChange={(e) =>
                      setMilestones(
                        milestones.map((x, j) =>
                          j === i ? { ...x, isPublic: e.target.checked } : x
                        )
                      )
                    }
                  />
                  <span>{t('competitions.calendar.public')}</span>
                </label>
                <Button
                  variant="ghost"
                  iconOnly
                  icon={<X />}
                  aria-label={t('competitions.calendar.remove', { label: m.label || i + 1 })}
                  onClick={() => setMilestones(milestones.filter((_, j) => j !== i))}
                />
              </div>
            ))}
            {!readOnly && (
              <div
                className="row"
                style={{ gap: 'var(--space-2)', justifyContent: 'space-between' }}
              >
                <Button
                  variant="secondary"
                  size="sm"
                  icon={<Plus />}
                  onClick={() =>
                    setMilestones([
                      ...milestones,
                      { label: '', startsAt: '', endsAt: null, isPublic: true },
                    ])
                  }
                >
                  {t('competitions.calendar.add')}
                </Button>
                <Button
                  variant="primary"
                  loading={saveMilestones.isPending}
                  onClick={() => saveMilestones.mutate()}
                >
                  {t('competitions.calendar.save')}
                </Button>
              </div>
            )}
          </fieldset>
        </CardBody>
      </Card>

      <Card>
        <CardHeader title={t('competitions.categories.title')} />
        <CardBody>
          <p className="text-muted" style={{ marginBlockStart: 0 }}>
            {t('competitions.categories.help', { seats: c.seats })}
          </p>
          <fieldset
            disabled={readOnly}
            className="stack"
            style={{ border: 0, padding: 0, gap: 'var(--space-3)' }}
          >
            {categories.map((cat, i) => (
              <div
                key={i}
                className="row"
                style={{ gap: 'var(--space-2)', flexWrap: 'wrap', alignItems: 'flex-end' }}
              >
                <Input
                  label={t('competitions.categories.code')}
                  mono
                  value={cat.code}
                  onChange={(e) =>
                    setCategories(
                      categories.map((x, j) =>
                        j === i ? { ...x, code: e.target.value.toUpperCase() } : x
                      )
                    )
                  }
                />
                <Input
                  label={t('competitions.categories.label')}
                  value={cat.label}
                  fieldClassName="grow"
                  onChange={(e) =>
                    setCategories(
                      categories.map((x, j) => (j === i ? { ...x, label: e.target.value } : x))
                    )
                  }
                />
                <Input
                  label={t('competitions.fields.seats')}
                  type="number"
                  min={1}
                  value={cat.seats ?? ''}
                  onChange={(e) =>
                    setCategories(
                      categories.map((x, j) =>
                        j === i
                          ? { ...x, seats: e.target.value ? Number(e.target.value) : null }
                          : x
                      )
                    )
                  }
                />
                <Button
                  variant="ghost"
                  iconOnly
                  icon={<X />}
                  aria-label={t('competitions.calendar.remove', { label: cat.label || i + 1 })}
                  onClick={() => setCategories(categories.filter((_, j) => j !== i))}
                />
              </div>
            ))}
            {!readOnly && (
              <div
                className="row"
                style={{ gap: 'var(--space-2)', justifyContent: 'space-between' }}
              >
                <Button
                  variant="secondary"
                  size="sm"
                  icon={<Plus />}
                  onClick={() =>
                    setCategories([...categories, { code: '', label: '', seats: null }])
                  }
                >
                  {t('competitions.categories.add')}
                </Button>
                <Button
                  variant="primary"
                  loading={saveCategories.isPending}
                  onClick={() => saveCategories.mutate()}
                >
                  {t('competitions.categories.save')}
                </Button>
              </div>
            )}
          </fieldset>
        </CardBody>
      </Card>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* Avis officiel                                                       */
/* ------------------------------------------------------------------ */

function NoticeTab({ c }: { c: CompetitionDetail }) {
  const { t } = useTranslation()
  const { session } = useAuth()
  const toast = useToast()
  const apply = useApply(c.id)
  const input = useRef<HTMLInputElement>(null)
  const versions = useQuery({
    queryKey: [...COMPETITIONS_KEY, c.id, 'notices'],
    queryFn: () =>
      session.api
        .get<{ data: Notice[] }>(`/admin/competitions/${c.id}/notices`)
        .then((r) => r.data),
  })
  const upload = useMutation({
    mutationFn: (file: File) => {
      const body = new FormData()
      body.append('file', file)
      return session.api.post<Notice>(`/admin/competitions/${c.id}/notices`, body)
    },
    onSuccess: async (notice) => {
      toast.success(t('competitions.notice.uploaded', { version: notice.version }))
      if (input.current) input.current.value = ''
      await versions.refetch()
      await apply()
    },
    onError: (error) =>
      toast.error(errorMessage(error), Object.values(fieldErrors(error)).join(' ')),
  })
  const open = useMutation({
    mutationFn: (version: number) =>
      session.api.download(`/admin/competitions/${c.id}/notices/${version}`),
    onSuccess: ({ blob }) => {
      const url = URL.createObjectURL(blob)
      window.open(url, '_blank', 'noopener')
      setTimeout(() => URL.revokeObjectURL(url), 60_000)
    },
    onError: (error) => toast.error(errorMessage(error)),
  })

  const columns: Column<Notice>[] = [
    { key: 'version', header: t('competitions.notice.version'), cell: (n) => `v${n.version}` },
    { key: 'file', header: t('competitions.notice.file'), cell: (n) => n.fileName },
    {
      key: 'size',
      header: t('competitions.notice.size'),
      cell: (n) => `${(n.size / 1024).toFixed(0)} Ko`,
    },
    { key: 'date', header: t('competitions.notice.date'), cell: (n) => when(n.createdAt) },
    {
      key: 'sha',
      header: 'SHA-256',
      cell: (n) => (
        <span className="perm-code" title={n.sha256}>
          {n.sha256.slice(0, 16)}…
        </span>
      ),
    },
    {
      key: 'open',
      header: '',
      align: 'end',
      cell: (n) => (
        <Button
          variant="ghost"
          size="sm"
          icon={<FileDown />}
          onClick={() => open.mutate(n.version)}
        >
          {t('competitions.notice.open')}
        </Button>
      ),
    },
  ]

  return (
    <Card>
      <CardHeader
        title={t('competitions.tabs.notice')}
        actions={
          c.can.update && (
            <>
              <input
                ref={input}
                type="file"
                accept="application/pdf,.pdf"
                className="sr-only"
                id="notice-file"
                onChange={(e) => {
                  const file = e.target.files?.[0]
                  if (file) upload.mutate(file)
                }}
              />
              <Button
                variant="primary"
                icon={<FileUp />}
                loading={upload.isPending}
                onClick={() => input.current?.click()}
              >
                {c.notice ? t('competitions.notice.replace') : t('competitions.notice.upload')}
              </Button>
            </>
          )
        }
      />
      <CardBody>
        <p className="text-muted" style={{ marginBlockStart: 0 }}>
          {t('competitions.notice.help')}
        </p>
      </CardBody>
      <Table
        columns={columns}
        rows={versions.data ?? []}
        rowKey={(n) => String(n.version)}
        loading={versions.isPending}
        caption={t('competitions.tabs.notice')}
        empty={<Empty title={t('competitions.notice.none')} />}
      />
    </Card>
  )
}

/* ------------------------------------------------------------------ */
/* Historique                                                          */
/* ------------------------------------------------------------------ */

function HistoryTab({ id }: { id: string }) {
  const { t } = useTranslation()
  const { session } = useAuth()
  const history = useQuery({
    queryKey: [...COMPETITIONS_KEY, id, 'history'],
    queryFn: () =>
      session.api
        .get<{ data: HistoryEntry[] }>(`/admin/competitions/${id}/history`)
        .then((r) => r.data),
  })
  const label = (s: string | null) =>
    s ? STATUS_LABELS.competition[s as keyof typeof STATUS_LABELS.competition].label : '—'
  return (
    <Card>
      <CardHeader title={t('competitions.tabs.history')} />
      <CardBody>
        {history.data?.length === 0 && <Empty title={t('competitions.history.empty')} />}
        <ol className="timeline">
          {history.data?.map((h) => (
            <li key={h.id}>
              <div style={{ fontWeight: 600 }}>
                {h.kind === 'transition' && `${label(h.from)} → ${label(h.to)}`}
                {h.kind === 'extension' &&
                  t('competitions.history.extension', {
                    before: when(h.metadata?.closesAt?.before),
                    after: when(h.metadata?.closesAt?.after),
                  })}
                {h.kind === 'derogation' &&
                  t(
                    h.metadata?.granted
                      ? 'competitions.history.derogationGranted'
                      : 'competitions.history.derogationRevoked',
                    {
                      candidate: h.metadata?.candidate,
                    }
                  )}
              </div>
              <div className="timeline-meta">
                {when(h.createdAt)} ·{' '}
                {h.system ? <Badge variant="neutral">{t('audit.system')}</Badge> : h.actor}
              </div>
              {h.reason && <p style={{ margin: 'var(--space-1) 0 0' }}>« {h.reason} »</p>}
            </li>
          ))}
        </ol>
      </CardBody>
    </Card>
  )
}

/* ------------------------------------------------------------------ */
/* Dérogations                                                         */
/* ------------------------------------------------------------------ */

function DerogationsTab({ c }: { c: CompetitionDetail }) {
  const { t } = useTranslation()
  const { session } = useAuth()
  const toast = useToast()
  const apply = useApply(c.id)
  const [granting, setGranting] = useState(false)
  const [revoking, setRevoking] = useState<Derogation | null>(null)
  const list = useQuery({
    queryKey: [...COMPETITIONS_KEY, c.id, 'derogations'],
    queryFn: () =>
      session.api
        .get<{ data: Derogation[] }>(`/admin/competitions/${c.id}/derogations`)
        .then((r) => r.data),
  })
  const revoke = useMutation({
    mutationFn: ({ d, reason }: { d: Derogation; reason: string }) =>
      session.api.delete(`/admin/competitions/${c.id}/derogations/${d.id}`, { body: { reason } }),
    onSuccess: async () => {
      setRevoking(null)
      toast.success(t('rbac.user.done'))
      await list.refetch()
      await apply()
    },
    onError: (error) => toast.error(errorMessage(error)),
  })

  const columns: Column<Derogation>[] = [
    { key: 'candidate', header: t('competitions.derogations.candidate'), cell: (d) => d.candidate },
    {
      key: 'until',
      header: t('competitions.derogations.until'),
      cell: (d) => (
        <span>
          {when(d.validUntil)}{' '}
          {d.active ? (
            <Badge variant="success">{t('competitions.derogations.active')}</Badge>
          ) : (
            <Badge variant="outline">
              {d.revokedAt ? t('rbac.user.revoked') : t('competitions.derogations.expired')}
            </Badge>
          )}
        </span>
      ),
    },
    {
      key: 'reason',
      header: t('rbac.user.reason'),
      cell: (d) => <span style={{ fontSize: 'var(--text-sm)' }}>{d.reason}</span>,
    },
    {
      key: 'by',
      header: t('rbac.user.grantedBy'),
      cell: (d) => (
        <span style={{ fontSize: 'var(--text-sm)' }}>
          {d.grantedBy}
          <br />
          {when(d.grantedAt)}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'end',
      cell: (d) =>
        d.active && c.can.derogation ? (
          <Button variant="ghost" size="sm" onClick={() => setRevoking(d)}>
            {t('rbac.user.revoke')}
          </Button>
        ) : null,
    },
  ]

  return (
    <Card>
      <CardHeader
        title={t('competitions.tabs.derogations')}
        actions={
          c.can.derogation &&
          ['open', 'closed', 'suspended'].includes(c.status) && (
            <Button variant="primary" size="sm" icon={<Plus />} onClick={() => setGranting(true)}>
              {t('competitions.derogations.grant')}
            </Button>
          )
        }
      />
      <CardBody>
        <p className="text-muted" style={{ marginBlockStart: 0 }}>
          {t('competitions.derogations.help')}
        </p>
      </CardBody>
      <Table
        columns={columns}
        rows={list.data ?? []}
        rowKey={(d) => d.id}
        loading={list.isPending}
        caption={t('competitions.tabs.derogations')}
        empty={<Empty title={t('competitions.derogations.none')} />}
      />
      <GrantDerogationModal
        open={granting}
        onClose={() => setGranting(false)}
        competitionId={c.id}
        onDone={async () => {
          await list.refetch()
          await apply()
        }}
      />
      <ConfirmModal
        open={revoking !== null}
        onClose={() => setRevoking(null)}
        title={t('competitions.derogations.revokeTitle')}
        message={revoking?.candidate}
        tone="danger"
        reasonRequired
        confirmLabel={t('rbac.user.revoke')}
        onConfirm={async (reason) => {
          if (revoking)
            await revoke.mutateAsync({ d: revoking, reason: reason ?? '' }).catch(() => undefined)
        }}
      />
    </Card>
  )
}

function GrantDerogationModal({
  open,
  onClose,
  competitionId,
  onDone,
}: {
  open: boolean
  onClose: () => void
  competitionId: string
  onDone: () => Promise<void>
}) {
  const { t } = useTranslation()
  const { session } = useAuth()
  const toast = useToast()
  const [form, setForm] = useState({ email: '', validUntil: '', reason: '' })
  const grant = useMutation({
    mutationFn: () =>
      session.api.post(`/admin/competitions/${competitionId}/derogations`, {
        email: form.email,
        validUntil: fromLocalInput(form.validUntil),
        reason: form.reason,
      }),
    onSuccess: async () => {
      toast.success(t('competitions.derogations.granted'))
      setForm({ email: '', validUntil: '', reason: '' })
      onClose()
      await onDone()
    },
    onError: (error) => {
      if (!Object.keys(fieldErrors(error)).length) toast.error(errorMessage(error))
    },
  })
  const errors = fieldErrors(grant.error)
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('competitions.derogations.grant')}
      description={t('competitions.derogations.grantText')}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('rbac.create.cancel')}
          </Button>
          <Button
            variant="primary"
            type="submit"
            form="derogation-form"
            loading={grant.isPending}
            disabled={!form.email || !form.validUntil || form.reason.trim().length < 10}
          >
            {t('competitions.derogations.grantSubmit')}
          </Button>
        </>
      }
    >
      <form
        id="derogation-form"
        className="stack"
        onSubmit={(e) => {
          e.preventDefault()
          grant.mutate()
        }}
      >
        <Input
          label={t('competitions.derogations.email')}
          type="email"
          required
          value={form.email}
          error={errors.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
        />
        <Input
          label={t('competitions.derogations.until')}
          type="datetime-local"
          required
          hint={t('competitions.derogations.untilHint')}
          value={form.validUntil}
          error={errors.validUntil}
          onChange={(e) => setForm({ ...form, validUntil: e.target.value })}
        />
        <Textarea
          label={t('rbac.user.reason')}
          required
          rows={3}
          hint={t('competitions.reasonHint')}
          value={form.reason}
          error={errors.reason}
          onChange={(e) => setForm({ ...form, reason: e.target.value })}
        />
      </form>
    </Modal>
  )
}
